import React from 'react';
import { t } from 'i18next';
import { Button, Col, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import type { PersonalDetailsInitialValues } from './types';
import type { PersonalDetailsValues } from './PersonalDetailsForm.form';
import { personalDetailsForm } from './PersonalDetailsForm.form';

import type { FormMode } from '@app/Components/Forms/EditableFormShell';
import { EditableFormShell } from '@app/Components/Forms/EditableFormShell';
import type { SelectOption } from '@app/Utilities/SelectMap';
import type { IndUpdateDTO } from '@api/DTO/Ind/Ind/dto';

import { SelectField } from '@app/Components/Forms/Fields/SelectField';
import { TextField } from '@app/Components/Forms/Fields/TextField';
import { SelectWithButtonField } from '@app/Components/Forms/Fields/SelectWithButtonField';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusCircle } from '@fortawesome/free-solid-svg-icons';

export type PersonalDetailsFormProps = {
  mode?: FormMode;
  initialValues?: Partial<PersonalDetailsInitialValues>;
  updatedIndSource: number;
  genderOptions: Array<SelectOption<string>>;
  sourceOptions: Array<SelectOption<number>>;
  loadingSourceOptions: boolean;
  isSaving?: boolean;
  onSubmit: (payload: IndUpdateDTO) => Promise<void> | void;
  onAddUserSource: () => void;
};

export const PersonalDetailsForm = ({
  mode = 'read',
  initialValues,
  updatedIndSource,
  genderOptions,
  sourceOptions,
  loadingSourceOptions,
  isSaving = false,
  onAddUserSource,
  onSubmit,
}: PersonalDetailsFormProps) => {
  const values = React.useMemo(() => personalDetailsForm.defaults(initialValues), [initialValues]);

  const form = useForm<PersonalDetailsValues>({
    resolver: zodResolver(personalDetailsForm.schema),
    defaultValues: values,
    mode: 'onBlur',
  });

  const { setValue } = form;

  const handleSubmit = async (values: PersonalDetailsValues) => {
    const parsed = personalDetailsForm.schema.parse(values);
    await onSubmit(personalDetailsForm.mapper(parsed));
  };

  const canEdit = !loadingSourceOptions;

  return (
    <EditableFormShell<PersonalDetailsValues>
      mode={mode}
      form={form}
      initialValues={values}
      canEdit={canEdit}
      canCancel={true}
      isSaving={isSaving}
      onSubmit={handleSubmit}
    >
      {() => (
        <Row>
          <Col md={3}>
            <SelectField
              name="indGender"
              label={t('agent.profile.personalDetailsForm.statusLabel')}
              options={genderOptions}
            />
          </Col>

          <Col md={3}>
            <SelectWithButtonField
              name="indSource"
              label={t('agent.profile.personalDetailsForm.sourceLabel')}
              options={sourceOptions}
              loading={loadingSourceOptions}
              button={
                <Button onClick={onAddUserSource} variant="primary" aria-label={t('common.add')}>
                  <FontAwesomeIcon icon={faPlusCircle} />
                </Button>
              }
            />
          </Col>

          <Col md={3}>
            <TextField
              name="indReferral"
              label={t('agent.profile.personalDetailsForm.referredByLabel')}
            />
          </Col>

          <Col md={3}>
            <TextField
              name="indReferralTo"
              label={t('agent.profile.personalDetailsForm.referredToLabel')}
            />
          </Col>
        </Row>
      )}
    </EditableFormShell>
  );
};
